import { BigNumber } from '@ethersproject/bignumber'
import { useWeb3 } from 'modules/blockChain/hooks/useWeb3'
import { weiToStr } from 'modules/blockChain/utils/parseWei'
import {
  ContractCollateralManager,
  ContractRociPayment,
} from 'modules/contracts/contracts'
import { useSWR } from 'modules/network/hooks/useSwr'
import { PoolAsset } from 'modules/pools/constants/poolAssets'
import { useAssetsData } from 'modules/pools/hooks/useAssetsData'
import { useLoansList } from 'modules/pools/hooks/useLoansList'
import { useMemo } from 'react'

type CollateralData = {
  asset: PoolAsset
  totalAmount: string
  lockedAmount: string
  availableAmount: string
}

type Data = Record<PoolAsset, CollateralData>

const COLLATERAL_ASSETS = [PoolAsset.WETH]

export const useCollateralData = () => {
  const { walletAddress, chainId, library } = useWeb3()
  const { getAssetDecimals } = useAssetsData()

  const loans = useLoansList()

  const loansDisplay = useMemo(
    () => loans.data?.filter(loan => loan.loan.status < 3),
    [loans.data],
  )

  const data = useSWR<Data | undefined>(
    `${chainId}-${walletAddress}-collateral-data-${loansDisplay?.length}`,
    async () => {
      if (!walletAddress) return

      const requests = COLLATERAL_ASSETS.map(async asset => {
        const assetDecimals = getAssetDecimals(asset)
        const rociPayment = ContractRociPayment.connectRpc({
          chainId,
        })
        const collateralManager = ContractCollateralManager.connectWeb3({
          chainId,
          library,
        })

        const totalCollateral = await collateralManager
          .getCollateralLookup(rociPayment.address, walletAddress)
          .then(totalCollateralData => totalCollateralData[1])
          .catch(() => BigNumber.from(0))

        const availableCollateral = await rociPayment
          .getMaxWithdrawableCollateral({ from: walletAddress })
          .then(r =>
            loansDisplay && loansDisplay.length > 0 ? r.mul(99).div(100) : r,
          )
          .catch(() => BigNumber.from(0))

        const lockedCollateral = totalCollateral.sub(availableCollateral)

        return {
          asset,
          totalAmount: weiToStr(totalCollateral, assetDecimals),
          availableAmount: weiToStr(availableCollateral, assetDecimals),
          lockedAmount: weiToStr(lockedCollateral, assetDecimals),
        }
      })

      const res = await Promise.all(requests)

      return res.reduce((result, { asset, ...assetData }) => {
        result[asset] = { ...assetData, asset }
        return result
      }, {} as Data)
    },
    {
      refreshInterval: 15000,
      revalidateOnFocus: true,
    },
  )

  return data
}
